import { useCallback } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import _get from 'lodash/get'

import { SignupFormType } from '@src/components/signup/types'
import { compressImage } from '@src/utils/image'
import { CheckboxChangeEvent } from 'antd/es/checkbox'

const useSignupContext = () => {
	const { register, formState, setValue, control, watch, getValues, clearErrors } =
		useFormContext<SignupFormType>()
	const {
		fields: contactsFields,
		append,
		remove,
	} = useFieldArray<SignupFormType, 'vendorContacts', 'id'>({ name: 'vendorContacts', control })
	const brandLogoData = watch('brandLogo')
	const businessFiles = watch(['businessRegistration', 'salesReport'])

	const handleAddVendorContact = () => {
		append({ name: '', position: '', email: '', phone: '' })
	}

	const handleRemoveVendorContact = (idx: number) => {
		remove(idx)
	}

	const handleClickCopyCeoInfoToFillContactInfo = (e: CheckboxChangeEvent) => {
		if (e.target.checked) {
			const ceoInfo = getValues(['ceoName', 'phone', 'email'])
			setValue('vendorContacts[0].name', ceoInfo[0])
			setValue('vendorContacts[0].phone', ceoInfo[1])
			setValue('vendorContacts[0].email', ceoInfo[2])
			setValue('vendorContacts[0].position', '대표')
			clearErrors('vendorContacts[0].position')
			if (ceoInfo[0]) clearErrors('vendorContacts[0].name')
			if (ceoInfo[1]) clearErrors('vendorContacts[0].phone')
			if (ceoInfo[2]) clearErrors('vendorContacts[0].email')
		}
	}

	const handleCompressImage = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>, destField: string) => {
			const { files } = e.target

			if (files?.[0]) {
				const compressedImage = await compressImage(files[0])
				setValue(destField, compressedImage)
			}
		},
		[setValue],
	)

	const getFieldProps = (fieldName: string, { ...otherProps } = {}) => {
		const { errors } = formState
		const fieldErr = _get(errors, fieldName)
		const errorMsg = typeof fieldErr?.message === 'string' ? fieldErr?.message : undefined
		return {
			...register(fieldName),
			errorMsg,
			isError: !!errorMsg,
			...otherProps,
		}
	}

	const getFileFieldProps = (fieldName: string, fileFieldName?: string, { ...otherProps } = {}) => {
		const { errors } = formState
		const fieldErr = _get(errors, fieldName)
		const errorMsg = typeof fieldErr?.message === 'string' ? fieldErr?.message : undefined
		const updateField = fileFieldName !== undefined ? fileFieldName : fieldName
		return {
			...register(fieldName, {
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleCompressImage(e, updateField),
			}),
			errorMsg,
			isError: !!errorMsg,
			...otherProps,
		}
	}

	return {
		contactsFields,
		handleAddVendorContact,
		handleRemoveVendorContact,
		handleClickCopyCeoInfoToFillContactInfo,
		brandLogoData,
		businessFiles,
		getFieldProps,
		getFileFieldProps,
	}
}

export default useSignupContext
